import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Spin, Tooltip } from 'antd';
import {
    FileSearchOutlined, ReloadOutlined, CopyOutlined, LoadingOutlined, DownOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import Toast from '../../helpers/Toast';
import { summarize } from '../../services/AiService';
import styles from './AiAssist.module.scss';

/**
 * Section inline (accordéon) affichant un résumé markdown généré par l'IA
 * pour une tâche ou un dossier. Le résumé est chargé paresseusement au
 * premier dépliage afin de ne pas solliciter l'IA à chaque affichage.
 *
 * @param {'tache'|'affaire'} type
 * @param {string} uid
 */
export default function AiSummarySection({ type, uid }) {
    const [open, setOpen]       = useState(false);
    const [loaded, setLoaded]   = useState(false);
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState('');
    const [error, setError]     = useState(null);
    const abortRef = useRef(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const text = await summarize(type, uid, controller.signal);
            setSummary(text || '');
            if (!text) setError('Résumé indisponible. Réessayez.');
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return;
            setError(err?.response?.data?.error ?? 'Résumé indisponible. Réessayez.');
        } finally {
            setLoading(false);
            setLoaded(true);
        }
    };

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !loaded && !loading) load();
        if (!next) abortRef.current?.abort();
    };

    const regenerate = (e) => {
        e.stopPropagation();
        load();
    };

    const copy = (e) => {
        e.stopPropagation();
        if (!summary) return;
        navigator.clipboard?.writeText(summary)
            .then(() => Toast.success('Résumé copié.'))
            .catch(() => Toast.error('Impossible de copier.'));
    };

    const titleLabel = type === 'affaire' ? 'Résumé du dossier' : 'Résumé de la tâche';

    return (
        <div className={`${styles.aiSection} ${open ? styles.isOpen : ''}`}>
            <div
                className={styles.aiSectionHead}
                onClick={toggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
            >
                <span className={styles.aiSectionTitleWrap}>
                    <FileSearchOutlined className={styles.aiSectionIcon} />
                    <span className={styles.aiSectionTitle}>{titleLabel}</span>
                    <span className={styles.aiBadge}>IA</span>
                </span>

                <span className={styles.aiSectionHeadRight}>
                    {open && loaded && !loading && summary && (
                        <Tooltip title="Copier">
                            <button type="button" className={styles.aiActionBtn} onClick={copy} aria-label="Copier le résumé">
                                <CopyOutlined />
                            </button>
                        </Tooltip>
                    )}
                    {open && loaded && !loading && (
                        <Tooltip title="Régénérer">
                            <button type="button" className={styles.aiActionBtn} onClick={regenerate} aria-label="Régénérer le résumé">
                                <ReloadOutlined />
                            </button>
                        </Tooltip>
                    )}
                    <DownOutlined className={`${styles.aiChevron} ${open ? styles.isOpen : ''}`} />
                </span>
            </div>

            {open && (
                <div className={styles.aiSectionBody}>
                    {loading ? (
                        <div className={styles.summaryLoading}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 26 }} spin />} />
                            <span>{"L'IA analyse les informations…"}</span>
                        </div>
                    ) : error ? (
                        <div>
                            <div className={styles.summaryError}>{error}</div>
                            <div style={{ marginTop: 8 }}>
                                <button
                                    type="button"
                                    className={styles.ghostAction}
                                    onClick={(e) => { e.stopPropagation(); load(); }}
                                >
                                    <ReloadOutlined style={{ marginRight: 5 }} />
                                    Réessayer
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.summaryContent}>
                            <ReactMarkdown>{summary}</ReactMarkdown>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

AiSummarySection.propTypes = {
    type: PropTypes.oneOf(['tache', 'affaire']).isRequired,
    uid: PropTypes.string.isRequired,
};
